import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('promaxo_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('promaxo_token');
      localStorage.removeItem('promaxo_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  users: () => api.get('/auth/users'),
  updateUser: (id, data) => api.put(`/auth/users/${id}`, data),
};

// Inventory
export const inventoryAPI = {
  list: (params) => api.get('/inventory/items', { params }),
  get: (id) => api.get(`/inventory/items/${id}`),
  create: (data) => api.post('/inventory/items', data),
  update: (id, data) => api.put(`/inventory/items/${id}`, data),
  locations: () => api.get('/inventory/locations'),
  stockAlerts: () => api.get('/inventory/stock-alerts'),
  createTransaction: (data) => api.post('/inventory/transactions', data),
  receive: (data) => api.post('/inventory/receive', data),
  dispositionLot: (lotId, data) => api.post(`/inventory/lots/${lotId}/disposition`, data),
  whereUsed: (id) => api.get(`/inventory/items/${id}/where-used`),
  transfer: (data) => api.post('/inventory/transfers', data),
};

// BOM
export const bomAPI = {
  list: (params) => api.get('/boms', { params }),
  get: (id) => api.get(`/boms/${id}`),
  create: (data) => api.post('/boms', data),
  update: (id, data) => api.put(`/boms/${id}`, data),
  approve: (id, data) => api.post(`/boms/${id}/approve`, data),
  release: (id, data) => api.post(`/boms/${id}/release`, data),
  addLine: (bomId, data) => api.post(`/boms/${bomId}/lines`, data),
  removeLine: (bomId, lineId) => api.delete(`/boms/${bomId}/lines/${lineId}`),
};

// ECO
export const ecoAPI = {
  list: (params) => api.get('/ecos', { params }),
  get: (id) => api.get(`/ecos/${id}`),
  create: (data) => api.post('/ecos', data),
  approve: (id, data) => api.post(`/ecos/${id}/approve`, data),
  transferToEvolve: (id) => api.post(`/ecos/${id}/transfer-evolve`),
};

// Work Orders
export const workOrderAPI = {
  list: (params) => api.get('/work-orders', { params }),
  get: (id) => api.get(`/work-orders/${id}`),
  create: (data) => api.post('/work-orders', data),
  advanceStage: (id, data) => api.post(`/work-orders/${id}/advance-stage`, data),
  passGate: (woId, gateId, data) => api.post(`/work-orders/${woId}/inspection-gates/${gateId}/pass`, data),
  failGate: (woId, gateId, data) => api.post(`/work-orders/${woId}/inspection-gates/${gateId}/fail`, data),
  backflush: (id, data) => api.post(`/work-orders/${id}/backflush`, data || {}),
};

// Suppliers
export const supplierAPI = {
  list: (params) => api.get('/suppliers', { params }),
  get: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
};

// Purchase Orders
export const purchaseOrderAPI = {
  list: (params) => api.get('/purchase-orders', { params }),
  get: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  approve: (id, data) => api.post(`/purchase-orders/${id}/approve`, data),
  receive: (id, data) => api.post(`/purchase-orders/${id}/receive`, data),
};

// MRP
export const mrpAPI = {
  run: (data) => api.post('/mrp/run', data || {}),
  runs: () => api.get('/mrp/runs'),
  suggestions: (params) => api.get('/mrp/suggestions', { params }),
  convertSuggestion: (id, data) => api.post(`/mrp/suggestions/${id}/convert`, data),
  simulate: (data) => api.post('/mrp/simulate', data || {}),
};

// Costing
export const costingAPI = {
  rollups: (params) => api.get('/costing/rollups', { params }),
  createRollup: (data) => api.post('/costing/rollups', data),
  standardCosts: (params) => api.get('/costing/standard-costs', { params }),
  createStandardCost: (data) => api.post('/costing/standard-costs', data),
  approveStandardCost: (id, data) => api.post(`/costing/standard-costs/${id}/approve`, data),
  summary: () => api.get('/costing/summary'),
};

// Quality
export const qualityAPI = {
  ncRecords: (params) => api.get('/quality/nc-records', { params }),
  getNc: (id) => api.get(`/quality/nc-records/${id}`),
  createNc: (data) => api.post('/quality/nc-records', data),
  updateNc: (id, data) => api.put(`/quality/nc-records/${id}`, data),
  dispositionNc: (id, data) => api.post(`/quality/nc-records/${id}/disposition`, data),
  capaRecords: (params) => api.get('/quality/capa-records', { params }),
  createCapa: (data) => api.post('/quality/capa-records', data),
  complaints: (params) => api.get('/quality/complaints', { params }),
  createComplaint: (data) => api.post('/quality/complaints', data),
};

// Analytics
export const analyticsAPI = {
  dashboard: () => api.get('/analytics/dashboard'),
  kpis: () => api.get('/analytics/kpis'),
};

// Audit
export const auditAPI = {
  logs: (params) => api.get('/audit', { params }),
};

// E-Signatures
export const signatureAPI = {
  sign: (data) => api.post('/esignatures/sign', data),
  get: (entityType, entityId) => api.get(`/esignatures/${entityType}/${entityId}`),
};

// Evolve
export const evolveAPI = {
  interfaceLogs: (params) => api.get('/evolve/interface-logs', { params }),
  mockBuildStatus: (data) => api.post('/evolve/mock-build-status', data),
  cocReferences: () => api.get('/evolve/coc-references'),
  status: () => api.get('/evolve/status'),
};

// Portal (Supplier)
const portalApi = axios.create({
  baseURL: `${API_BASE}/api/portal`,
  headers: { 'Content-Type': 'application/json' },
});
portalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('portal_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
export const portalAPI = {
  login: (data) => portalApi.post('/auth/login', data),
  purchaseOrders: () => portalApi.get('/purchase-orders'),
  acknowledgePO: (poId, data) => portalApi.post(`/purchase-orders/${poId}/acknowledge`, data || {}),
  submitASN: (poId, data) => portalApi.post(`/purchase-orders/${poId}/asn`, data),
  listASNs: () => portalApi.get('/asns'),
  uploadDocument: (data) => portalApi.post('/documents', data),
  listDocuments: () => portalApi.get('/documents'),
};

// AI
export const aiAPI = {
  chat: (data) => api.post('/ai/chat', data),
  chatHistory: (params) => api.get('/ai/chat-history', { params }),
  complianceScan: () => api.post('/ai/compliance-scan'),
  agentStatus: () => api.get('/ai/agents/status'),
  ecoImpact: (data) => api.post('/ai/eco-impact', data),
  demandForecast: (data) => api.post('/ai/demand-forecast', data || {}),
  ncCorrelation: (data) => api.post('/ai/nc-correlation', data || {}),
  bomSuggest: (data) => api.post('/ai/bom-suggest', data),
  costAnomaly: (data) => api.post('/ai/cost-anomaly', data || {}),
  prDrafts: (data) => api.post('/ai/pr-drafts', data || {}),
};

// Notifications
export const notificationAPI = {
  list: () => api.get('/notifications'),
};

// Exports
export const exportAPI = {
  auditCsv: (params) => api.get('/exports/audit-csv', { params, responseType: 'blob' }),
  complianceReport: () => api.get('/exports/compliance-report'),
  traceabilityMatrix: () => api.get('/exports/traceability-matrix'),
  compliancePdf: () => api.get('/exports/compliance-pdf', { responseType: 'blob' }),
  traceabilityPdf: () => api.get('/exports/traceability-pdf', { responseType: 'blob' }),
  auditPdf: (params) => api.get('/exports/audit-pdf', { params, responseType: 'blob' }),
  dailySummaryPdf: () => api.get('/exports/daily-summary-pdf', { responseType: 'blob' }),
};

// Readiness
export const readinessAPI = {
  checklist: () => api.get('/readiness/checklist'),
};

// Reports
export const reportsAPI = {
  dailySummary: () => api.get('/reports/daily-summary'),
  scheduleRun: (data) => api.post('/reports/schedule-run', data),
  scheduled: (params) => api.get('/reports/scheduled', { params }),
};

// Validation
export const validationAPI = {
  generateBundle: () => api.post('/validation/bundle', {}),
  listBundles: () => api.get('/validation/bundles'),
  getBundle: (id) => api.get(`/validation/bundles/${id}`),
  downloadBundlePdf: (id) => api.get(`/validation/bundles/${id}/pdf`, { responseType: 'blob' }),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
};
